import {TreeNodeProps} from '@priority-software/priority-style-react';
import {texts} from '../texts';

type ReplacementType = 'titleToId' | 'idToTitle';
type FormatType = 'text' | 'html';

const findIdByTitle = (tree: TreeNodeProps[], title: string = ''): string | null => {
    // id= formTitle::columnTitle
    const splitTitle = title.split('.');
    const formTitle = splitTitle[0];
    const columnTitle = splitTitle[1];
    for (const node of tree) {
        if (node?.title === columnTitle && node?.extraData?.formTitle === formTitle) {
            return node.id;
        }
        if (node?.nodes) {
            const result = findIdByTitle(node.nodes, title);
            if (result) return result;
        }
    }
    return null;
};

const findTitleById = (tree: TreeNodeProps[], id: string = ''): string | null => {
    for (const node of tree) {
        if (node?.id === id) {
            return node.title;
        }
        if (node?.nodes) {
            const result = findTitleById(node.nodes, id);
            if (result) return result;
        }
    }
    return null;
};

const ignoreRulesTags = (text: string): boolean => {
    const tags = [texts.rule_number, texts.rule_description];
    return tags.some(tag => text.includes(tag));
};

/**
 * This function gets input and if this input has tags in it, the function will change the data according to the format and type.
 * This function is used for clarifying the data as seen by the user in the editor from the data that is in the database.
 * If the user sees <<formTitle::columnTitle>> in the editor, the function will change it to <<formName.columnName>> for savinge in the server and vice versa.
 * @example For: input: <<ORDERS.CUSTNAME>>, type: idToTitle, format: text; the output will be <<Sale orders::Customer Name>>
 * @example For: input: <<Sale orders::Customer Name>>, type: titleToId, format: text; the output will be <<ORDERS.CUSTNAME>>
 */
const replaceTagsData = (
    input: string,
    treeValues: TreeNodeProps,
    type: ReplacementType,
    inputFormat: FormatType,
    outputFormat: FormatType
): string => {
    if (!input) return input;
    /**
     * tags logic: the server should always get the data in double less than greater then signs : <<data>>
     * the client side should change it depend on the way of the data is displayed.
     * if the data is the subject than it displayed as <<data>>
     * if the data is in the body (html editor) than it displayed as &lt;&lt;data&gt;&gt;
     */

    const htmlOpenTag = '&lt;&lt;';
    const htmlCloseTag = '&gt;&gt;';
    const textOpenTag = '<<';
    const textCloseTag = '>>';
    const openTag = outputFormat === 'html' ? htmlOpenTag : textOpenTag;
    const closeTag = outputFormat === 'html' ? htmlCloseTag : textCloseTag;
    const regex =
        inputFormat === 'text'
            ? new RegExp(
                  `${textOpenTag.replace(/&/g, '&')}([^<>]+)${textCloseTag.replace(/&/g, '&')}`,
                  'g'
              )
            : new RegExp(
                  `${htmlOpenTag.replace(/&/g, '&')}([^<>]+)${htmlCloseTag.replace(/&/g, '&')}`,
                  'g'
              );

    return input.replace(regex, (_, id) => {
        if (ignoreRulesTags(id)) return `${openTag}${id}${closeTag}`;

        if (type === 'titleToId') {
            const result = findIdByTitle([treeValues], id);
            return result ? `${openTag}${result}${closeTag}` : `${openTag}${id}${closeTag}`;
        } else {
            const formTitle = findTitleById([treeValues], id.split('.')[0]);
            const columnTitle = findTitleById([treeValues], id);
            const result = formTitle ? `${formTitle}.${columnTitle}` : '';
            return result ? `${openTag}${result}${closeTag}` : `${openTag}${id}${closeTag}`;
        }
    });
};

export const replaceTitlesWithIds = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, 'titleToId', 'text', 'text');

export const replaceIdsWithTitles = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, 'idToTitle', 'text', 'text');

export const replaceTitlesWithIdsForHtml = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, 'titleToId', 'html', 'text');

export const replaceIdsWithTitlesForHtml = (input: string, treeValues: TreeNodeProps): string =>
    replaceTagsData(input, treeValues, 'idToTitle', 'text', 'html');
